import { skipToken, useLazyQuery, useQuery } from "@apollo/client/react";
import { Stack, styled } from "@mui/material";
import type { DialogProps } from "@toolpad/core";
import {
  Autocomplete,
  Button,
  InputLabel,
  Modal,
  Loader,
  MenuItem,
  TextInput,
  Typography,
} from "@trimble/modus-cmx";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import DeviceIcon from "@/components/DeviceIcon";
import AddDeviceSearchResult from "@/components/Devices/AddDeviceSearchResult";
import { TOAST_AUTO_HIDE_DURATION } from "@/constants";
import { gql } from "@/gql";
import type { AddDeviceSearchQuery } from "@/gql/graphql";
import { DeviceSearchOutcome, OptInStatus } from "@/gql/graphql";
import { useRequestDeviceTransfer } from "@/hooks/service/useRequestDeviceTransfer";
import { useRequestOptIn } from "@/hooks/service/useRequestOptIn";
import { useAppNotifications } from "@/hooks/useAppNotifications";
import {
  PermissionAction,
  PermissionCategory,
  PermissionGate,
} from "@/permissions";
import { getModusEventTarget } from "@/utils/modusEvents";
import { showErrorToast } from "@/utils/showErrorToast";
import { isGqlType } from "@/utils/typeGuards";

const ADD_DEVICE_MODELS = gql(`
  query AddDeviceModels {
    deviceModels {
      id
      description
      iconUrl
      modelName
      partNumber
    }
  }
`);

const ADD_DEVICE_SEARCH = gql(`
  query AddDeviceSearch($modelName: String!, $serialNumber: String!) {
    deviceSearch(modelName: $modelName, serialNumber: $serialNumber) {
      outcome
      dealerDevice {
        id
        description
        iconUrl
        isExternal
        modelName
        serialNumber
        owner {
          id
          companyName
          ... on DealerCustomer {
            optInStatus
          }
        }
      }
    }
  }
`);

const StyledModal = styled(Modal)(() => ({
  ".modus-wc-modal-box": {
    overflowY: "visible",
  },
  ".modus-wc-autocomplete .modus-wc-menu": {
    width: "max-content",
    minWidth: "100%",
  },
}));

const MODEL_INPUT_ID = "add-device-model";
const SERIAL_NUMBER_INPUT_ID = "add-device-serial-number";

type AddDeviceModalActionsProps = {
  result: AddDeviceSearchQuery["deviceSearch"];
  note: string;
  onClose: () => void;
  onViewDevice: (serialNumber: string) => void;
};

function AddDeviceModalActions({
  result,
  note,
  onClose,
  onViewDevice,
}: AddDeviceModalActionsProps) {
  const { t } = useTranslation();
  const notifications = useAppNotifications();
  const [requestOptIn, { loading: requestingOptIn }] = useRequestOptIn({
    successMessage: t("devices.add_device_modal.opt_in_request_sent"),
  });
  const [requestDeviceTransfer, { loading: requestingTransfer }] =
    useRequestDeviceTransfer();
  const device = result.dealerDevice;
  const customerOwner =
    device?.owner != null && isGqlType(device.owner, "DealerCustomer")
      ? device.owner
      : null;

  const handleRequestOptIn = async () => {
    if (customerOwner == null) {
      return;
    }
    if (customerOwner.optInStatus === OptInStatus.Requested) {
      notifications.show(t("devices.add_device_modal.opt_in_request_pending"), {
        severity: "info",
        autoHideDuration: TOAST_AUTO_HIDE_DURATION,
      });
      onClose();
      return;
    }
    const { error } = await requestOptIn({
      variables: { customerAccountId: customerOwner.id },
    });
    if (error == null) {
      onClose();
    }
  };

  const handleRequestTransfer = async () => {
    if (device == null) {
      return;
    }
    try {
      await requestDeviceTransfer(device, note);
    } catch {
      return;
    }
    onViewDevice(device.serialNumber);
  };

  const primaryAction = ((): React.ReactNode => {
    switch (result.outcome) {
      case DeviceSearchOutcome.TransferEligible:
        return (
          <PermissionGate
            resource={PermissionCategory.Device}
            action={PermissionAction.CreateAndManage}
          >
            <Button
              color="primary"
              variant="filled"
              size="sm"
              loading={requestingTransfer}
              disabled={device == null || requestingTransfer}
              onButtonClick={() => {
                void handleRequestTransfer();
              }}
            >
              {t("devices.add_device_modal.add_device")}
            </Button>
          </PermissionGate>
        );
      case DeviceSearchOutcome.OptInRequired:
        return (
          <PermissionGate
            resource={PermissionCategory.Customer}
            action={PermissionAction.OptInAndManage}
          >
            <Button
              size="sm"
              loading={requestingOptIn}
              disabled={customerOwner == null || requestingOptIn}
              onButtonClick={() => {
                void handleRequestOptIn();
              }}
            >
              {t("devices.add_device_modal.request_opt_in")}
            </Button>
          </PermissionGate>
        );
      case DeviceSearchOutcome.Found:
        return (
          <Button
            size="sm"
            disabled={device == null}
            onButtonClick={() => {
              if (device != null) {
                onViewDevice(device.serialNumber);
              }
            }}
          >
            {t("devices.add_device_modal.view_device")}
          </Button>
        );
      case DeviceSearchOutcome.NotFound:
      case DeviceSearchOutcome.UnableToAdd:
        return null;
    }
  })();

  if (primaryAction == null) {
    return null;
  }

  return (
    <>
      <Button
        variant="filled"
        color="tertiary"
        size="sm"
        onButtonClick={onClose}
      >
        {t("devices.add_device_modal.cancel")}
      </Button>
      {primaryAction}
    </>
  );
}

type AddDeviceModalResult = string | undefined;

function AddDeviceModal({
  open,
  onClose,
}: DialogProps<undefined, AddDeviceModalResult>) {
  const { t } = useTranslation();
  const notifications = useAppNotifications();
  const [note, setNote] = useState("");
  const [modelId, setModelId] = useState("");
  const [modelSearch, setModelSearch] = useState<string | null>(null);
  const [modelSelectionCount, setModelSelectionCount] = useState(0);
  const [serialNumber, setSerialNumber] = useState("");
  const { data, error, loading } = useQuery(
    ADD_DEVICE_MODELS,
    open ? { fetchPolicy: "cache-and-network" } : skipToken
  );
  const [searchDevice, { data: searchData, loading: searching }] = useLazyQuery(
    ADD_DEVICE_SEARCH,
    { fetchPolicy: "network-only" }
  );
  const selectedModel = data?.deviceModels.find(
    (model) => model.id === modelId
  );
  const seenModelNames = new Set<string>();
  const uniqueDeviceModels = (data?.deviceModels ?? []).filter((model) => {
    if (seenModelNames.has(model.modelName)) {
      return false;
    }

    seenModelNames.add(model.modelName);
    return true;
  });
  uniqueDeviceModels.sort((a, b) => a.modelName.localeCompare(b.modelName));
  const normalizedModelSearch = (modelSearch ?? "").trim().toLowerCase();
  const visibleDeviceModels = uniqueDeviceModels.filter((model) =>
    model.modelName.toLowerCase().includes(normalizedModelSearch)
  );
  const handleClose = () => {
    void onClose(undefined);
  };
  const handleViewDevice = (deviceSerialNumber: string) => {
    void onClose(deviceSerialNumber);
  };

  return (
    <StyledModal
      open={open}
      onClose={handleClose}
      modalId="add-device-modal"
      data-testid="add-device-modal"
      width={600}
    >
      <div slot="header">
        <Typography hierarchy="h4" size="lg" weight="bold">
          {t("devices.add_device_modal.title")}
        </Typography>
      </div>
      <div slot="content">
        <Stack spacing={2}>
          <Typography hierarchy="p">
            {t("devices.add_device_modal.description")}
          </Typography>
          {error != null && data == null ? (
            <Typography>
              {t("devices.add_device_modal.load_models_error")}
            </Typography>
          ) : (
            <Stack gap={0.5}>
              <Stack direction="row" gap={0.5} alignItems="flex-end">
                <Stack spacing={0.5}>
                  <InputLabel
                    forId={MODEL_INPUT_ID}
                    labelText={t("devices.model_name")}
                    required
                  />
                  <Autocomplete
                    key={modelSelectionCount}
                    inputId={MODEL_INPUT_ID}
                    required
                    placeholder={t("devices.add_device_modal.select_model")}
                    showMenuOnFocus
                    size="md"
                    debounceMs={0}
                    value={modelSearch ?? selectedModel?.modelName}
                    disabled={loading && data == null}
                    onInputChange={(event) => {
                      const text =
                        getModusEventTarget(event, HTMLInputElement)?.value ??
                        "";
                      setModelSearch(text);
                      if (text !== selectedModel?.modelName) {
                        setModelId("");
                      }
                    }}
                    onItemSelect={(event) => {
                      setModelSearch(null);
                      setModelSelectionCount((count) => count + 1);
                      setModelId(event.detail.value);
                    }}
                    onClearClick={() => {
                      setModelSearch("");
                      setModelId("");
                    }}
                    include-clear
                    include-search
                  >
                    <div slot="menu-items">
                      {visibleDeviceModels.map((model) => (
                        <MenuItem
                          key={model.id}
                          label={model.modelName}
                          subLabel={model.description ?? undefined}
                          value={model.id}
                          selected={model.id === modelId}
                        >
                          <div slot="start-icon">
                            <DeviceIcon iconUrl={model.iconUrl} />
                          </div>
                        </MenuItem>
                      ))}
                    </div>
                  </Autocomplete>
                </Stack>
                <Stack spacing={0.5}>
                  <InputLabel
                    forId={SERIAL_NUMBER_INPUT_ID}
                    labelText={t("devices.serial_number")}
                    required
                  />
                  <TextInput
                    inputId={SERIAL_NUMBER_INPUT_ID}
                    required
                    value={serialNumber}
                    style={{ display: "flex", width: "100%" }}
                    placeholder={t(
                      "devices.add_device_modal.search_serial_number"
                    )}
                    onInputChange={(event) => {
                      setSerialNumber(event.target.value);
                    }}
                  />
                </Stack>
                <Button
                  key={searching ? "search-loading" : "search-idle"}
                  color="primary"
                  variant="outlined"
                  size="md"
                  disabled={!modelId || serialNumber.trim() === "" || searching}
                  style={{ display: "flex", marginBottom: "5px" }}
                  onButtonClick={() => {
                    if (selectedModel == null) {
                      return;
                    }
                    searchDevice({
                      variables: {
                        modelName: selectedModel.modelName,
                        serialNumber: serialNumber.trim(),
                      },
                    }).catch((searchError: unknown) => {
                      showErrorToast(
                        notifications,
                        searchError,
                        t("devices.add_device_modal.search_error")
                      );
                    });
                  }}
                >
                  {searching ? (
                    <Loader variant="spinner" size="xs" />
                  ) : (
                    <span>{t("devices.add_device_modal.search")}</span>
                  )}
                </Button>
              </Stack>
            </Stack>
          )}
          {searchData && (
            <AddDeviceSearchResult
              result={searchData.deviceSearch}
              note={note}
              onNoteChange={setNote}
            />
          )}
        </Stack>
      </div>
      <div
        slot="footer"
        style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
      >
        {searchData && (
          <AddDeviceModalActions
            result={searchData.deviceSearch}
            note={note}
            onClose={handleClose}
            onViewDevice={handleViewDevice}
          />
        )}
      </div>
    </StyledModal>
  );
}

export default AddDeviceModal;
